import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  IconUser, IconPlus, IconX, IconAlertTriangle, IconWhatsApp, IconShield,
} from "../components/icons";

// ── tipos ─────────────────────────────────────────────────────────────────────

type UserRole = "admin" | "gestor" | "consultor" | "visualizador";
type UserStatus = "active" | "inactive" | "pending";

type ManagedUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  whatsappNumber: string | null;
  lastLoginAt: string | null;
  createdAt: string;
};

// ── helpers visuais ───────────────────────────────────────────────────────────

const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Administrador",
  gestor: "Gestor",
  consultor: "Consultor",
  visualizador: "Visualizador",
};

const STATUS_CONFIG: Record<UserStatus, { label: string; badge: string }> = {
  active:   { label: "Ativo",    badge: "bg-tm-50 text-tm-700 border-tm-200" },
  inactive: { label: "Inativo",  badge: "bg-gray-100 text-gray-500 border-gray-200" },
  pending:  { label: "Pendente", badge: "bg-warning-50 text-warning-700 border-warning-200" },
};

function StatusBadge({ status }: { status: UserStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.inactive;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.badge}`}>
      {cfg.label}
    </span>
  );
}

const INPUT_CLS =
  "w-full text-sm border border-surface-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400";

// ── formulário (criação + edição) ─────────────────────────────────────────────

type FormState = {
  name: string;
  email: string;
  password: string;
  whatsappNumber: string;
  role: UserRole;
  status: UserStatus;
};

function UserForm({
  editing,
  onSuccess,
  onCancel,
}: {
  editing: ManagedUser | null;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const isEdit = editing !== null;
  const [form, setForm] = useState<FormState>({
    name: editing?.name ?? "",
    email: editing?.email ?? "",
    password: "",
    whatsappNumber: editing?.whatsappNumber ?? "",
    role: editing?.role ?? "gestor",
    status: editing?.status ?? "active",
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return api.put(`/users/${editing.id}`, {
          name: form.name,
          whatsappNumber: form.whatsappNumber,
          role: form.role,
          status: form.status,
        });
      }
      return api.post("/users", {
        name: form.name,
        email: form.email,
        password: form.password,
        whatsappNumber: form.whatsappNumber,
        role: form.role,
      });
    },
    onSuccess,
  });

  return (
    <div className="card p-5 border-tm-200 border-2">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-gray-900">
          {isEdit ? `Editar usuário — ${editing.name}` : "Novo usuário"}
        </p>
        <button onClick={onCancel} className="btn-ghost p-1.5 text-gray-400">
          <IconX className="w-4 h-4" />
        </button>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
            <input
              required type="text" value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Nome completo" className={INPUT_CLS}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">E-mail *</label>
            <input
              required type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="usuario@empresa.com"
              disabled={isEdit}
              className={`${INPUT_CLS} ${isEdit ? "bg-gray-50 text-gray-400" : ""}`}
            />
            {isEdit && <p className="text-xs text-gray-400 mt-0.5">O e-mail não pode ser alterado.</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <span className="inline-flex items-center gap-1">
                <IconWhatsApp className="w-3.5 h-3.5 text-tm-600" /> Número WhatsApp
              </span>
            </label>
            <input
              type="tel" value={form.whatsappNumber}
              onChange={(e) => set("whatsappNumber", e.target.value)}
              placeholder="+5548999991234" className={INPUT_CLS}
            />
            <p className="text-xs text-gray-400 mt-0.5">
              Com DDI e DDD. Quem tem número aqui pode conversar com a IA. Deixe vazio para bloquear.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Perfil de acesso</label>
            <select
              value={form.role}
              onChange={(e) => set("role", e.target.value as UserRole)}
              className={INPUT_CLS}
            >
              {(Object.keys(ROLE_LABEL) as UserRole[]).map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
              ))}
            </select>
          </div>
        </div>

        {isEdit ? (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => set("status", e.target.value as UserStatus)}
              className={`${INPUT_CLS} max-w-xs`}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="pending">Pendente</option>
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Senha de acesso ao painel *</label>
            <input
              required type="password" value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder="Mínimo 8 caracteres" minLength={8}
              className={`${INPUT_CLS} max-w-xs`}
            />
          </div>
        )}

        {mutation.isError && (
          <p className="text-sm text-danger-600 flex items-center gap-1.5">
            <IconAlertTriangle className="w-4 h-4" />
            {mutation.error instanceof ApiError ? mutation.error.message : "Erro ao salvar usuário"}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={mutation.isPending} className="btn-primary">
            {mutation.isPending ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar usuário"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── linha da tabela ───────────────────────────────────────────────────────────

function UserRow({
  user,
  isSelf,
  onEdit,
  onDelete,
}: {
  user: ManagedUser;
  isSelf: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-t border-surface-border hover:bg-surface-muted/40">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-tm-100 flex items-center justify-center flex-shrink-0">
            <IconUser className="w-4 h-4 text-tm-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user.name}{isSelf && <span className="text-xs text-gray-400 font-normal"> (você)</span>}
            </p>
            <p className="text-xs text-gray-400 truncate">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {user.whatsappNumber ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 font-mono">
            <IconWhatsApp className="w-3.5 h-3.5 text-tm-500" />
            {user.whatsappNumber}
          </span>
        ) : (
          <span className="text-xs text-gray-400 italic">sem número — não conversa</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{ROLE_LABEL[user.role]}</td>
      <td className="px-4 py-3"><StatusBadge status={user.status} /></td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button onClick={onEdit} className="btn-ghost text-xs text-tm-600 hover:text-tm-700 px-2 py-1">
          Editar
        </button>
        <button
          onClick={onDelete}
          disabled={isSelf}
          title={isSelf ? "Você não pode excluir o seu próprio usuário" : "Excluir usuário"}
          className="btn-ghost text-xs text-danger-600 hover:text-danger-700 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Excluir
        </button>
      </td>
    </tr>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function UsersPage() {
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [editing, setEditing] = useState<ManagedUser | null>(null);

  const isAdmin = me?.role === "admin";

  const { data, isLoading, error } = useQuery<{ users: ManagedUser[] }>({
    queryKey: ["users"],
    queryFn: () => api.get("/users"),
    enabled: isAdmin,
    staleTime: 30_000,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/users/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["users"] });
  const closeForm = () => { setIsAdding(false); setEditing(null); };
  const onFormSuccess = () => { closeForm(); refresh(); };

  const handleDelete = (u: ManagedUser) => {
    if (!window.confirm(`Excluir o usuário "${u.name}"? Esta ação não pode ser desfeita.`)) return;
    remove.mutate(u.id);
  };

  if (!isAdmin) {
    return (
      <div className="card p-8 text-center">
        <IconShield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-700">Acesso restrito</p>
        <p className="text-xs text-gray-400 mt-1">Apenas administradores podem gerenciar usuários.</p>
      </div>
    );
  }

  const users = data?.users ?? [];
  const showForm = isAdding || editing !== null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Usuários</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Cadastre quem acessa o painel e quem pode conversar com a IA pelo WhatsApp.
            O número informado aqui é o que libera o chat.
          </p>
        </div>
        {!showForm && (
          <button onClick={() => setIsAdding(true)} className="btn-primary text-xs py-2">
            <IconPlus className="w-3.5 h-3.5" />
            Novo usuário
          </button>
        )}
      </div>

      {/* Formulário */}
      {showForm && (
        <UserForm editing={editing} onSuccess={onFormSuccess} onCancel={closeForm} />
      )}

      {remove.isError && (
        <p className="text-sm text-danger-600 flex items-center gap-1.5">
          <IconAlertTriangle className="w-4 h-4" />
          {remove.error instanceof ApiError ? remove.error.message : "Erro ao excluir usuário"}
        </p>
      )}

      {/* Tabela */}
      {isLoading ? (
        <div className="card p-5 animate-pulse space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}
        </div>
      ) : error ? (
        <div className="card p-6 text-center">
          <IconAlertTriangle className="w-7 h-7 text-danger-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            {error instanceof ApiError ? error.message : "Não foi possível carregar os usuários."}
          </p>
        </div>
      ) : users.length === 0 ? (
        <div className="py-12 text-center border-2 border-dashed border-surface-border rounded-xl">
          <IconUser className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">Nenhum usuário cadastrado</p>
          <p className="text-xs text-gray-400 mt-1">Clique em "Novo usuário" para começar.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 bg-surface-muted/50">
                <th className="px-4 py-2.5">Usuário</th>
                <th className="px-4 py-2.5">WhatsApp</th>
                <th className="px-4 py-2.5">Perfil</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === me?.id}
                  onEdit={() => { setIsAdding(false); setEditing(u); }}
                  onDelete={() => handleDelete(u)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Nota explicativa */}
      <div className="card p-5 border-info-100">
        <div className="flex items-start gap-3">
          <IconWhatsApp className="w-4 h-4 text-tm-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-1">Como liberar o chat por WhatsApp</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              A IA só responde números cadastrados no campo <strong>WhatsApp</strong> de um usuário.
              Use o formato internacional <code className="bg-gray-100 px-1 rounded">+5548999991234</code> (DDI + DDD + número).
              Para revogar o acesso de alguém, basta editar o usuário e apagar o número — ou excluir o usuário.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
